import {
  getPositionLiqPrice,
  getUpdatedAccountOnFill,
} from '@/features/account/utils/math';
import { Account, Position, useAccountStore } from '@/store/use-account-store';
import {
  COLLATERAL_DECIMALS,
  OrderBook,
  useMarketStore,
} from '@/store/use-markets-store';
import {
  adjustDecimals,
  BigDecimal,
  bigIntToDecimalStr,
} from '@/utils/value-format';
import { FEE_DECIMALS } from '@/features/markets/api/get-exchange-info';
import { OrderFormSchema } from '../components';
import { getDefaultBigDecimal } from '@/lib/utils';
import { getIsStatusEval } from '@/features/vest-capital/utils/status';
import { getInitMarginRatio } from '@/features/markets/utils/helpers';

import { validateLimitPrice } from './helpers';
import {
  getMidPriceSlippage,
  getProjectedMidSlippage,
  getVisibleFill,
} from './slippage-estimation';

export const getEstFillPrice = (
  isBuy: boolean,
  size: bigint,
  book: OrderBook,
  priceDecimals: bigint,
  fillAtBbo = getIsStatusEval(
    useAccountStore
      .getState()
      .getCapitalStatus(useAccountStore.getState().address),
  ),
):
  | {
      bigint: bigint | null;
      decimal: string | null;
      enoughLiquidity: boolean;
      visibleCost: bigint;
      visibleFilled: bigint;
    }
  | undefined => {
  const levels = isBuy ? book.asks : book.bids;
  if (size === 0n) return;

  const positiveLevels = levels.filter(([, quantity]) => quantity > 0n);
  if (positiveLevels.length === 0) {
    return {
      bigint: null,
      decimal: null,
      enoughLiquidity: false,
      visibleCost: 0n,
      visibleFilled: 0n,
    };
  }

  const {
    remaining,
    visibleCost: cost,
    visibleFilled: consumed,
  } = getVisibleFill(positiveLevels, size);

  // For non-evaluation accounts, only displayed levels are used; the VWAP is
  // taken over the visible fill and prices beyond the displayed book are
  // never synthesized.
  const price = fillAtBbo ? positiveLevels[0][0] : cost / consumed;

  return {
    bigint: price,
    decimal: bigIntToDecimalStr(price, priceDecimals),
    enoughLiquidity: remaining === 0n,
    visibleCost: cost,
    visibleFilled: consumed,
  };
};

export const getFee = (
  symbol: string,
  size: bigint,
  price: bigint,
  options?: {
    address?: string;
  },
): BigDecimal => {
  const { marketSpec } = useMarketStore(symbol).getState();
  const capitalStatus = useAccountStore
    .getState()
    .getCapitalStatus(options?.address ?? useAccountStore.getState().address);
  const isEval = getIsStatusEval(capitalStatus);

  if (isEval || size === 0n || !marketSpec.takerFee) {
    return getDefaultBigDecimal();
  }

  const fee = adjustDecimals(
    price * marketSpec.takerFee * size,
    COLLATERAL_DECIMALS -
      marketSpec.priceDecimals -
      marketSpec.sizeDecimals -
      FEE_DECIMALS,
  );

  return {
    bigint: fee,
    decimal: bigIntToDecimalStr(fee, COLLATERAL_DECIMALS),
  };
};

export const getSlippage = (
  symbol: string,
  isBuy: boolean,
  openPrice: bigint,
  options?: {
    size: bigint;
    visibleCost: bigint;
    visibleFilled: bigint;
    book: OrderBook;
  },
): BigDecimal | undefined => {
  if (options) {
    const slippage = getProjectedMidSlippage(
      isBuy,
      options.size,
      options.visibleCost,
      options.visibleFilled,
      options.book,
    );
    if (slippage === null) return;

    return {
      bigint: slippage,
      decimal: bigIntToDecimalStr(slippage, 4n),
    };
  }

  const midPrice = useMarketStore(symbol).getState().marketData.midPrice;
  const slippage = getMidPriceSlippage(isBuy, openPrice, midPrice);
  if (slippage === null) return;

  return {
    bigint: slippage,
    decimal: bigIntToDecimalStr(slippage, 4n),
  };
};

export const getBboPrice = (symbol: string, isBuy: boolean): bigint | null => {
  const { book } = useMarketStore(symbol).getState();
  const side = isBuy ? 'asks' : 'bids';

  return book[side].find(([, quantity]) => quantity > 0n)?.[0] ?? null;
};

export const calculateSlippageAmount = (
  isBuy: boolean,
  size: bigint,
  fillPrice: bigint | null,
  referencePrice: bigint | null,
): bigint | null => {
  if (
    fillPrice === null ||
    fillPrice <= 0n ||
    referencePrice === null ||
    referencePrice <= 0n
  ) {
    return null;
  }

  return (
    size * (isBuy ? fillPrice - referencePrice : referencePrice - fillPrice)
  );
};

export const getOrderValue = (
  symbol: string,
  size: bigint,
  price: bigint,
): BigDecimal => {
  const { marketSpec } = useMarketStore(symbol).getState();
  if (size === 0n)
    return {
      bigint: 0n,
      decimal: bigIntToDecimalStr(
        0n,
        marketSpec.sizeDecimals + marketSpec.priceDecimals,
      ),
    };
  const orderValue = size * price;
  return {
    bigint: orderValue,
    decimal: bigIntToDecimalStr(
      orderValue,
      marketSpec.sizeDecimals + marketSpec.priceDecimals,
    ),
  };
};

export const getOrderValueFromPrice = (
  symbol: string,
  size: bigint,
  price: bigint,
): BigDecimal | undefined => {
  if (size === 0n) return;
  const orderValue = size * price;
  const { marketSpec } = useMarketStore(symbol).getState();
  return {
    bigint: orderValue,
    decimal: bigIntToDecimalStr(
      orderValue,
      marketSpec.sizeDecimals + marketSpec.priceDecimals,
    ),
  };
};

export const getLimitPriceFromMaxSlippage = (
  isBuy: boolean,
  maxSlippagePct: bigint, // pcts should always be 4 decimal precision
  price: bigint,
  priceDecimals: bigint,
): BigDecimal => {
  const limitPrice =
    ((10n ** 4n + (isBuy ? maxSlippagePct : -maxSlippagePct)) * price) /
    10n ** 4n;
  return {
    bigint: limitPrice,
    decimal: bigIntToDecimalStr(limitPrice, priceDecimals),
  };
};

export const getSlippageReferencePrice = (
  symbol: string,
  isBuy: boolean,
): bigint => {
  const { address, getCapitalStatus } = useAccountStore.getState();
  if (getIsStatusEval(getCapitalStatus(address))) {
    return getBboPrice(symbol, isBuy) ?? 0n;
  }

  return useMarketStore(symbol).getState().marketData.midPrice;
};

export const getMaxLeverage = (
  initMarginRatio: bigint,
): BigDecimal | undefined => {
  if (initMarginRatio === 0n) return undefined;
  const maxLeverage = initMarginRatio
    ? BigInt((10n ** 6n / initMarginRatio + 99n) / 100n) * 100n
    : 0n;
  return {
    bigint: maxLeverage,
    decimal: bigIntToDecimalStr(maxLeverage, 2n),
  };
};

// returns sizeDecimals
export const getSizeFromNumeraire = (
  symbol: string,
  numeraireSize: bigint, // expects in collateral decimals
  price: bigint,
  roundUp: boolean = true,
): bigint => {
  if (numeraireSize === 0n || price === 0n) return 0n;
  const { sizeDecimals, priceDecimals } =
    useMarketStore(symbol).getState().marketSpec;
  const decimalsPadding = COLLATERAL_DECIMALS + 1n;
  const bigSize = adjustDecimals(
    (numeraireSize * 10n ** decimalsPadding) / price,
    sizeDecimals - (COLLATERAL_DECIMALS - priceDecimals),
  ); // sizeDecimals + decimalsPadding
  const size = adjustDecimals(bigSize, -decimalsPadding);
  if (roundUp) {
    return bigSize > 0n && size == 0n ? 1n : size; // round up
  } else {
    return size;
  }
};

// TODO: limit prices
export const getEstLiqPrice = (
  symbol: string,
  isBuy: boolean,
  size: bigint,
  account: Account,
  orderInitMarginRatio?: bigint,
  price?: bigint,
): BigDecimal | undefined => {
  if (size === 0n) return;
  const { marketData } = useMarketStore(symbol).getState();
  const updatedAccount = getUpdatedAccountOnFill(
    symbol,
    isBuy,
    size,
    price ?? marketData.markPrice,
    0n,
    orderInitMarginRatio ?? getInitMarginRatio(symbol),
    account,
  );
  return getPositionLiqPrice(symbol, updatedAccount);
};

// Assumes always reducing position
export const getPriceFromPnl = (
  symbol: string,
  pnl: bigint, // in collateral decimals
  size: bigint,
  pos: Pick<Position, 'cost' | 'isLong' | 'size'>,
): BigDecimal => {
  const cost = size === pos.size ? pos.cost : (pos.cost * size) / pos.size;
  const signedCost = (pos.isLong ? 1n : -1n) * cost;
  const signedSize = (pos.isLong ? 1n : -1n) * size;
  const { marketSpec } = useMarketStore(symbol).getState();
  const price =
    (adjustDecimals(
      pnl,
      marketSpec.sizeDecimals + marketSpec.priceDecimals - COLLATERAL_DECIMALS,
    ) +
      signedCost) /
    signedSize;
  return {
    bigint: price,
    decimal: bigIntToDecimalStr(price, marketSpec.priceDecimals),
  };
};

export const generateScaleOrders = (
  rawOrder: OrderFormSchema,
  sizeString: string,
): OrderFormSchema[] => {
  const {
    startPrice: startPriceString,
    endPrice: endPriceString,
    ordersCount: ordersCountString,
    sizeSkew: sizeSkewString,
  } = rawOrder;

  const size = Number(sizeString);
  const startPrice = Number(startPriceString);
  const endPrice = Number(endPriceString);
  const ordersCount = Number(ordersCountString);
  const sizeSkew = Number(sizeSkewString);

  if (
    !(
      size > 0 &&
      startPrice > 0 &&
      endPrice > 0 &&
      ordersCount > 0 &&
      sizeSkew > 0
    )
  ) {
    return [];
  }

  const prices: string[] = [];
  const priceStep: number =
    ordersCount > 1
      ? (endPrice - startPrice) / (ordersCount - 1)
      : endPrice - startPrice;
  const weights: number[] = [];
  let weightSum: number = 0;

  for (let i = 0; i < ordersCount; i++) {
    const limitPrice = validateLimitPrice(
      rawOrder.order.symbol,
      (startPrice + priceStep * i).toString(),
    );
    prices.push(limitPrice);
    const weight =
      ordersCount > 1 ? Math.pow(sizeSkew, i / (ordersCount - 1)) : 1;

    weightSum += weight;
    weights.push(weight);
  }

  const normalizedWeights = weights.map((w) => w / (weightSum || 1));

  return normalizedWeights.map((weight, index) => ({
    ...rawOrder,
    order: {
      ...rawOrder.order,
      size: (size * weight).toString(),
      limitPrice: prices[index],
    },
  }));
};
